import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Beat } from '../../types/content';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import { useAuth } from '../../context/AuthContext';
import VerseRecorder from '../verses/VerseRecorder';
import RecordingAudioCheckModal from '../verses/RecordingAudioCheckModal';
import {
  isRecordingAudioCheckPassed,
  setRecordingAudioCheckPassed,
} from '../../utils/recordingAudioCheckSession';
import { CypherBeat } from '../../hooks/useCypherChallenge';

interface Props {
  beat: CypherBeat;
  index: number;
  onRecorded: () => void;
}

const CypherTrackCard: React.FC<Props> = ({ beat, index, onRecorded }) => {
  const { authState } = useAuth();
  const { play, pause, stop, isPlaying, currentAudioKey, currentTime, duration, formatTime } =
    useAudioPlayer();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [showRecorder, setShowRecorder] = useState(false);
  const [showAudioCheck, setShowAudioCheck] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  const role = authState.user?.role;
  const canRecord = role === 'rapper' || role === 'producer_rapper';
  const slotsLeft = Math.max(0, (beat.verses_count || 0) - beat.verse_entries);
  const atCapacity = slotsLeft <= 0;
  const isThisPlaying = isPlaying && currentAudioKey === `cypher-${beat.id}`;

  const handlePlayPause = () => {
    if (!audioRef.current || !beat.file_url) {
      setAudioError('Audio unavailable');
      return;
    }
    setAudioError(null);
    if (isThisPlaying) {
      pause();
    } else {
      void play(audioRef.current, `cypher-${beat.id}`).catch(() => {
        setAudioError('Unable to play this beat');
      });
    }
  };

  const openRecorder = () => {
    stop();
    if (!canRecord || atCapacity) return;
    if (isRecordingAudioCheckPassed()) {
      setShowRecorder(true);
    } else {
      setShowAudioCheck(true);
    }
  };

  const progress =
    isThisPlaying && duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  return (
    <article className="cypher-track">
      <div className="cypher-track__index" aria-hidden>
        {String(index + 1).padStart(2, '0')}
      </div>

      <div className="cypher-track__main">
        <div className="cypher-track__meta">
          <h3 className="cypher-track__title">{beat.title}</h3>
          <p className="cypher-track__by">
            {beat.producer?.username || 'Producer'}
            {beat.genre ? ` · ${beat.genre}` : ''}
            {beat.bpm ? ` · ${beat.bpm} BPM` : ''}
          </p>
          <div className="cypher-track__stats">
            <span>
              {beat.verse_entries} / {beat.verses_count || '—'} in the cypher
            </span>
            {atCapacity && <span className="cypher-track__full">Session full</span>}
          </div>
        </div>

        <div className="cypher-track__player">
          <button
            type="button"
            className={`cypher-track__play ${isThisPlaying ? 'cypher-track__play--active' : ''}`}
            onClick={handlePlayPause}
            aria-label={isThisPlaying ? 'Pause beat' : 'Play beat'}
          >
            {isThisPlaying ? '❚❚' : '▶'}
          </button>
          <div className="cypher-track__progress-wrap" aria-hidden>
            <div className="cypher-track__progress" style={{ width: `${progress}%` }} />
          </div>
          <span className="cypher-track__time">
            {isThisPlaying ? formatTime(currentTime) : formatTime(0)}
          </span>
        </div>

        {audioError && <p className="cypher-track__error">{audioError}</p>}

        <div className="cypher-track__actions">
          {canRecord ? (
            <button
              type="button"
              className="cypher-track__cta"
              onClick={openRecorder}
              disabled={atCapacity}
            >
              {atCapacity ? 'Cypher full' : 'Drop bars'}
            </button>
          ) : (
            <Link to={`/beats/${beat.id}`} className="cypher-track__cta cypher-track__cta--ghost">
              {role === 'listener' ? 'Listen & vote' : 'Open track'}
            </Link>
          )}
          <Link to={`/beats/${beat.id}`} className="cypher-track__link">
            Full session →
          </Link>
        </div>
      </div>

      {beat.file_url && (
        <audio ref={audioRef} src={beat.file_url} preload="metadata" className="hidden" />
      )}

      <RecordingAudioCheckModal
        open={showAudioCheck}
        onClose={() => setShowAudioCheck(false)}
        onPassed={() => {
          setRecordingAudioCheckPassed();
          setShowAudioCheck(false);
          setShowRecorder(true);
        }}
        beatTitle={beat.title}
      />

      {showRecorder &&
        createPortal(
          <VerseRecorder
            beat={beat as Beat}
            requireAudioCheck
            onClose={() => setShowRecorder(false)}
            onSuccess={() => {
              setShowRecorder(false);
              onRecorded();
            }}
          />,
          document.body
        )}
    </article>
  );
};

export default CypherTrackCard;
